import { boxShadows, corners, media, typography } from "../../styles";
import styled, { css } from "styled-components";
import { applyOpacity } from "../../styles/utils";
import React from "react";
export type SelectableButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  selected?: boolean;
};
const unselectedStyles = css`
  background: ${({
  theme
}) => theme.brand.cssVars["--d-surface"]};
  border: 2px solid ${({
  theme
}) => theme.brand.cssVars["--d-border"]};
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
  @media ${media.hoverable} {
    &:hover {
      /*
       * A subtle tint wash behind the darker border-color change below —
       * gives unselected/hovered dates & timeslots a "this is about to be
       * selected" preview state distinct from the flat --d-surface resting
       * state, using --d-primary's dedicated light-tint token rather than
       * an ad-hoc opacity of --d-primary itself.
       */
      background: ${({
  theme
}) => theme.brand.cssVars["--d-tint"]};
      border-color: ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
      /*
       * --d-tint is always a light colour, even on dark-theme brands (see
       * theme.ts's BRAND_DEFAULTS — dark.primaryLight is still a pale
       * blue), whereas --d-text flips to a light cream/white colour on the
       * dark bucket. Leaving "color" as the inherited --d-text above meant
       * dark-theme hover showed near-white text on this pale tint
       * background — unreadable. --d-on-tint is computed the same way as
       * --d-on-action (best-contrast text against the paired colour), so
       * it stays legible against --d-tint regardless of theme bucket.
       */
      color: ${({
  theme
}) => theme.brand.cssVars["--d-on-tint"]};
    }
  }
`;
const selectedStyles = css`
  border: 2px solid ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
  background: ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
  color: ${({
  theme
}) => theme.brand.cssVars["--d-on-action"]};
  @media ${media.hoverable} {
    &:hover {
      filter: brightness(0.94);
    }
  }
`;

/**
 * Renders a button that can be selected. Behaviour is essentially the same as a
 * radio button, but styled as and used as a button. Usage examples include Date
 * selection in the CalendarDatePicker and Timeslot selection for the
 * Availability step.
 */
export const SelectableButton = styled.button<SelectableButtonProps>`
  ${typography.smallBold}
  ${corners.m}

  @media ${media.hoverable} {
    &:hover,
    &:active {
      cursor: pointer;
    }
  }

  &:focus-visible {
    /*
     * Was "&:focus" — plain :focus also matches after a mouse click (native
     * <button> elements retain focus on click in every major browser), so
     * every date SELECTION via mouse was also painting this ring, not just
     * genuine keyboard navigation. :focus-visible uses the browser's own
     * input-modality heuristic to only match for keyboard/programmatic
     * focus, which is exactly the behaviour boxShadows.focusRing's WCAG
     * 2.4.7 justification actually calls for ("Focus Visible") — it was
     * never meant to fire on every click.
     *
     * Was background: --d-tint; color: --d-text; (and, before that,
     * background: --d-primary; color: --d-on-action;) — both drew the
     * focus indicator INSIDE the button using a colour derived from the
     * button's own brand colour, so visibility depended entirely on that
     * brand colour (e.g. barely visible on pale pink/amber brand primaries,
     * or on dark-theme surfaces). boxShadows.focusRing draws a fixed
     * two-colour (white/black) ring entirely OUTSIDE the button instead —
     * see its definition for the WCAG 3:1 contrast proof.
     *
     * "position: relative" + "z-index: 1" lift this button (and its ring)
     * above its normal-flow grid siblings. The calendar grid (DateSelector's
     * Container) has zero gap between date cells, so this ring — drawn 2-4px
     * outside the border box — always overlaps into neighbouring cells.
     * Without the stacking bump, sibling cells later in DOM order (to the
     * right/below) paint over the ring, while unavailable-date siblings
     * (DateButton's unavailableStyles sets "background: transparent") don't
     * paint over it at all — so depending on which neighbours happen to be
     * unavailable/later-in-DOM, only PART of the ring survives, rendering
     * as a stray partial arc/corner-bracket rather than a clean full ring.
     * Elevating stacking order guarantees the ring always paints complete,
     * on top of every neighbour, regardless of their background or DOM
     * position.
     */
    position: relative;
    z-index: 1;
    ${boxShadows.focusRing}
  }

  &:focus-visible {
    border-color: ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
  }

  &:active {
    color: ${({
  theme
}) => applyOpacity(theme.brand.cssVars["--d-text"], 0.7, false)};
    border-color: ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
  }

  ${({
  selected
}) => selected ? selectedStyles : unselectedStyles}
`;
export default SelectableButton;